import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { Nullable, UserWithToken } from '@ygoargentina/sdk/api/api.types';

// =============================================================================
// Types
// =============================================================================

export type StreamMatchStatus = 'PENDING' | 'STARTED' | 'FINISHED';

export type StreamMatch = {
  id: number;
  organizerId: number;
  player1Name: string;
  player2Name: string;
  player1Deck: string | null;
  player2Deck: string | null;
  bestOf: number;
  startingLp: number;
  game1WinnerSide: number | null;
  game2WinnerSide: number | null;
  game3WinnerSide: number | null;
  winnerSide: number | null;
  status: StreamMatchStatus;
  joinCode: string;
  liveOnOverlay: boolean;
  createdAt: string;
};

export type CreateStreamMatchInput = {
  organizerId: number;
  player1Name: string;
  player2Name: string;
  player1Deck?: string | null;
  player2Deck?: string | null;
  bestOf?: number;
  startingLp?: number;
};

export type UpdateStreamMatchInput = {
  player1Name?: string;
  player2Name?: string;
  player1Deck?: string | null;
  player2Deck?: string | null;
  bestOf?: number;
};

// =============================================================================
// Fragment
// =============================================================================

const STREAM_MATCH_FIELDS = `
  id
  organizerId
  player1Name
  player2Name
  player1Deck
  player2Deck
  bestOf
  startingLp
  game1WinnerSide
  game2WinnerSide
  game3WinnerSide
  winnerSide
  status
  joinCode
  liveOnOverlay
  createdAt
`;

// =============================================================================
// Queries
// =============================================================================

export const getStreamMatch = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch | null }>(
    gql`
      query streamMatch($id: Int!) {
        data: streamMatch(id: $id) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { id }
  );

export const getStreamMatchByCode = async (
  user: Nullable<UserWithToken>,
  code: string
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch | null }>(
    gql`
      query streamMatchByCode($code: String!) {
        data: streamMatchByCode(code: $code) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { code }
  );

export const myStreamMatches = async (
  user: Nullable<UserWithToken>,
  organizerId: number
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch[] }>(
    gql`
      query myStreamMatches($organizerId: Int!) {
        data: myStreamMatches(organizerId: $organizerId) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { organizerId }
  );

// =============================================================================
// Mutations
// =============================================================================

export const createStreamMatch = async (
  user: Nullable<UserWithToken>,
  input: CreateStreamMatchInput
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch }>(
    gql`
      mutation createStreamMatch($input: CreateStreamMatchInput!) {
        data: createStreamMatch(input: $input) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { input }
  );

export const updateStreamMatch = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: UpdateStreamMatchInput
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch }>(
    gql`
      mutation updateStreamMatch($id: Int!, $input: UpdateStreamMatchInput!) {
        data: updateStreamMatch(id: $id, input: $input) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { id, input }
  );

export const setLiveStreamMatch = async (
  user: Nullable<UserWithToken>,
  organizerId: number,
  id?: number | null
) =>
  getGraphQLQueryClient(user).request<{ data: boolean }>(
    gql`
      mutation setLiveStreamMatch($id: Int, $organizerId: Int!) {
        data: setLiveStreamMatch(id: $id, organizerId: $organizerId)
      }
    `,
    { id: id ?? null, organizerId }
  );

export const reportStreamMatchGame = async (
  user: Nullable<UserWithToken>,
  id: number,
  game: number,
  winnerSide?: number | null,
  code?: string | null
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch }>(
    gql`
      mutation reportStreamMatchGame($id: Int!, $game: Int!, $winnerSide: Int, $code: String) {
        data: reportStreamMatchGame(id: $id, game: $game, winnerSide: $winnerSide, code: $code) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { id, game, winnerSide: winnerSide ?? null, code: code ?? null }
  );

export const finishStreamMatch = async (
  user: Nullable<UserWithToken>,
  id: number,
  winnerSide?: number | null,
  code?: string | null
) =>
  getGraphQLQueryClient(user).request<{ data: StreamMatch }>(
    gql`
      mutation finishStreamMatch($id: Int!, $winnerSide: Int, $code: String) {
        data: finishStreamMatch(id: $id, winnerSide: $winnerSide, code: $code) {
          ${STREAM_MATCH_FIELDS}
        }
      }
    `,
    { id, winnerSide: winnerSide ?? null, code: code ?? null }
  );

export const deleteStreamMatch = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<{ data: boolean }>(
    gql`
      mutation deleteStreamMatch($id: Int!) {
        data: deleteStreamMatch(id: $id)
      }
    `,
    { id }
  );
